import { useEffect, useState, useCallback } from "react";
import { Loader2, TrendingUp, Info } from "lucide-react";
import {
  ComposedChart, Area, Line, ReferenceDot,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import api from "@/lib/api";
import SafeBoundary from "@/components/SafeBoundary";

/**
 * Kompakte Amortisations-Vorschau – zeigt kumulierte Ersparnis + Investitions-
 * Referenz-Linie über 15 Jahre. Wird eingebettet in `Kfw458Block`, damit der
 * Kunde die ROI-Auswirkung der KfW-Förderung direkt sieht, ohne zu scrollen.
 *
 * Daten kommen live von `/projects/:id/economics` (dieselbe Quelle wie der
 * volle `WirtschaftlichkeitBlock` weiter unten auf der Seite).
 */
const fmtCurrency = (n) => {
  try {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
  } catch {
    return `${Math.round(Number(n) || 0)} €`;
  }
};
const fmtCompact = (n) => {
  try {
    return new Intl.NumberFormat("de-DE", { notation: "compact", maximumFractionDigits: 1 }).format(Number(n) || 0) + " €";
  } catch {
    return `${Math.round(Number(n) || 0)} €`;
  }
};

export default function AmortizationMiniChart({ projectId, previewGross = 0, previewEigenanteil = null, refreshKey = 0, onJumpToDetail }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [debouncedGross, setDebouncedGross] = useState(previewGross);
  const [debouncedEigenanteil, setDebouncedEigenanteil] = useState(previewEigenanteil);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedGross(previewGross), 350);
    return () => clearTimeout(t);
  }, [previewGross]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedEigenanteil(previewEigenanteil), 350);
    return () => clearTimeout(t);
  }, [previewEigenanteil]);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (debouncedGross > 0) params.preview_gross = debouncedGross;
      // Live-Preview: sobald der User im KfW-Formular Änderungen macht,
      // wird der frisch berechnete Eigenanteil hier durchgereicht — der
      // Server überschreibt damit den gespeicherten Wert und die
      // Amortisations-Kurve reagiert sofort.
      if (debouncedEigenanteil !== null && debouncedEigenanteil !== undefined) {
        params.preview_eigenanteil = debouncedEigenanteil;
      }
      const r = await api.get(`/projects/${projectId}/economics`, { params });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, debouncedGross, debouncedEigenanteil]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return (
      <div className="bg-[#09090B] border border-zinc-800 rounded-xl p-4 text-zinc-300 text-xs flex items-center gap-2" data-testid="kfw-amort-mini-loading">
        <Loader2 className="animate-spin" size={12} /> Lade Amortisations-Vorschau…
      </div>
    );
  }
  if (!data) return null;
  const c = data.calculation || {};
  if (!c.applicable || c.no_investment) return null;

  const yt = Array.isArray(c.years_table) ? c.years_table : [];
  if (yt.length === 0) return null;

  const investEur = Number(c.investment?.eur || 0);
  const chartData = yt.map((y) => ({
    year: Number(y.year || 0),
    offset: Number(y.offset || 0),
    cum: Number(y.cum_savings_eur || 0),
    invest: investEur,
  }));
  const payback = Number(c.payback_year_index || 0);
  const paybackRow = payback && chartData[payback - 1] ? chartData[payback - 1] : null;
  const netto15 = Number(c.totals_15y?.net_savings_after_invest_eur || 0);

  return (
    <SafeBoundary>
      <div className="bg-[#09090B] border border-[#00E575]/25 rounded-xl p-4" data-testid="kfw-amort-mini">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <TrendingUp size={14} className="text-emerald-300 flex-shrink-0" />
            <div className="min-w-0">
              <h4 className="text-[14px] font-semibold text-zinc-100 leading-tight">Ihre Amortisation mit dieser Förderung</h4>
              <p className="text-[12px] text-zinc-400 font-medium mt-0.5">
                Live-Vorschau · Investitionsbasis <b className="text-zinc-100 font-semibold">{fmtCurrency(investEur)}</b>
                {c.investment?.basis && <span className="text-zinc-500"> ({c.investment.basis})</span>}
              </p>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-[12px] uppercase tracking-wider text-zinc-400 font-semibold">Amortisation</div>
            <div className="text-[16px] font-semibold text-emerald-300 font-mono leading-tight">
              {payback ? `nach ${payback} J.` : "> 15 J."}
            </div>
          </div>
        </div>

        <div style={{ width: "100%", height: 180 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: -12 }}>
              <defs>
                <linearGradient id="kfwMiniGreen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00E575" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#00E575" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#27272A" strokeDasharray="2 2" vertical={false} />
              <XAxis
                dataKey="year"
                stroke="#71717A"
                tick={{ fontSize: 9, fill: "#A1A1AA" }}
                tickFormatter={(v, i) => (i === 0 || i === chartData.length - 1 || i % 3 === 0 ? v : "")}
                axisLine={{ stroke: "#3F3F46" }}
                tickLine={false}
              />
              <YAxis
                stroke="#71717A"
                tick={{ fontSize: 9, fill: "#A1A1AA" }}
                tickFormatter={fmtCompact}
                axisLine={false}
                tickLine={false}
                width={48}
              />
              <Tooltip
                contentStyle={{ backgroundColor: "#18181B", border: "1px solid #3F3F46", borderRadius: 8, fontSize: 11 }}
                labelStyle={{ color: "#A1A1AA", fontWeight: 600, marginBottom: 2 }}
                itemStyle={{ color: "#E4E4E7" }}
                formatter={(v, name) => [fmtCurrency(v), name === "cum" ? "Kumulierte Ersparnis" : "Investition"]}
                labelFormatter={(label) => `Jahr ${label}`}
              />
              <Legend
                wrapperStyle={{ fontSize: 9, color: "#A1A1AA", paddingTop: 2 }}
                iconType="line"
                formatter={(v) => v === "cum" ? "Kumulierte Ersparnis" : "Investition"}
              />
              <Area
                type="monotone"
                dataKey="cum"
                stroke="#00E575"
                strokeWidth={2}
                fill="url(#kfwMiniGreen)"
                dot={{ fill: "#00E575", stroke: "#0F0F12", strokeWidth: 1, r: 2 }}
                activeDot={{ r: 4, stroke: "#fff", strokeWidth: 1 }}
                name="cum"
              />
              <Line
                type="monotone"
                dataKey="invest"
                stroke="#71717A"
                strokeWidth={1.3}
                strokeDasharray="4 3"
                dot={false}
                activeDot={false}
                name="invest"
              />
              {paybackRow && (
                <ReferenceDot
                  x={paybackRow.year}
                  y={paybackRow.cum}
                  r={5}
                  fill="#00E575"
                  stroke="#fff"
                  strokeWidth={2}
                  isFront
                  label={{
                    value: `${payback} J.`,
                    position: "top",
                    fill: "#00E575",
                    fontSize: 10,
                    fontWeight: 700,
                    offset: 6,
                  }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-zinc-800">
          <span className="text-[12px] text-zinc-300 font-medium flex items-center gap-1">
            <Info size={11} className="text-zinc-500" />
            Netto-Ersparnis nach 15 J.: <b className="text-emerald-300 font-semibold">{fmtCurrency(netto15)}</b>
          </span>
          {onJumpToDetail && (
            <button
              type="button"
              onClick={onJumpToDetail}
              data-testid="kfw-amort-mini-jump"
              className="text-[12px] font-semibold text-emerald-300 hover:text-emerald-200 hover:underline"
            >
              ↓ Details
            </button>
          )}
        </div>
      </div>
    </SafeBoundary>
  );
}
